import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import useSWR, { useSWRConfig } from "swr";
import * as adminApi from "@/api/admin";
import type { AdminSession, AdminUser } from "@/api/types";
import { onUnauthorized } from "@/lib/api-client";
import { getSession, setSession } from "./session";

type AuthContextValue = {
	user: AdminUser | null;
	isAuthenticated: boolean;
	login: (input: { email: string; password: string }) => Promise<void>;
	logout: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

/** Admin tokens last 12 hours; `/admin/me` re-validates on load and any 401 logs out. */
export function AuthProvider({ children }: { children: React.ReactNode }) {
	const [session, setState] = useState<AdminSession | null>(getSession);
	const { mutate } = useSWRConfig();

	const commit = useCallback((next: AdminSession | null) => {
		setSession(next);
		setState(next);
	}, []);

	const logout = useCallback(() => {
		commit(null);
		// Drop every cached admin response so the next admin doesn't see stale data.
		void mutate(() => true, undefined, { revalidate: false });
	}, [commit, mutate]);

	useEffect(
		() =>
			onUnauthorized((error) => {
				if (!getSession()) return;
				logout();
				toast(error.status === 403 ? "Admin access removed" : "Session expired", {
					description: error.status === 403 ? error.message : "Please sign in again.",
				});
			}),
		[logout],
	);

	useSWR(session ? adminApi.meKey : null, adminApi.getMe, {
		onSuccess: ({ user }) => {
			const current = getSession();
			if (current) commit({ ...current, user });
		},
	});

	const value = useMemo<AuthContextValue>(
		() => ({
			user: session?.user ?? null,
			isAuthenticated: !!session?.token,
			login: async (input) => commit(await adminApi.login(input)),
			logout,
		}),
		[session, commit, logout],
	);

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
	const context = useContext(AuthContext);
	if (!context) throw new Error("useAuth must be used inside <AuthProvider>");
	return context;
}
